import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { useToast } from '../hooks/useToast'

function whatsappUrl(telefone) {
  const digits = (telefone || '').replace(/\D/g, '')
  const number = digits.startsWith('55') ? digits : '55' + digits
  return `https://wa.me/${number}`
}

export default function Fornecedores() {
  const [fornecedores, setFornecedores] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editForn, setEditForn] = useState(null)
  const [form, setForm] = useState({ nome: '', telefone: '' })
  const [saving, setSaving] = useState(false)
  const [confirm, setConfirm] = useState(null)
  const { msg, visible, show } = useToast()

  const [fornItens, setFornItens] = useState([])
  const [loadingItens, setLoadingItens] = useState(false)
  const [editingCusto, setEditingCusto] = useState(null)
  const [custoInput, setCustoInput] = useState('')

  useEffect(() => { fetchFornecedores() }, [])

  async function fetchFornecedores() {
    setLoading(true)
    const { data } = await supabase.from('fornecedores').select('*').order('nome')
    setFornecedores(data || [])
    setLoading(false)
  }

  async function fetchFornItens(fornId) {
    setLoadingItens(true)
    const { data } = await supabase
      .from('item_fornecedores')
      .select('*, itens(nome, categoria, unidade)')
      .eq('fornecedor_id', fornId)
      .order('itens(nome)')
    setFornItens(data || [])
    setLoadingItens(false)
  }

  function openForm(forn = null) {
    setEditForn(forn)
    setForm(forn ? { nome: forn.nome, telefone: forn.telefone || '' } : { nome: '', telefone: '' })
    setFornItens([])
    setEditingCusto(null)
    setCustoInput('')
    if (forn) fetchFornItens(forn.id)
    setShowForm(true)
  }

  function closeForm() {
    setShowForm(false)
    setEditForn(null)
    setFornItens([])
    setEditingCusto(null)
  }

  async function salvar() {
    if (!form.nome.trim()) { show('Preencha o nome'); return }
    setSaving(true)
    const payload = { nome: form.nome.trim(), telefone: form.telefone.trim() || null }
    if (editForn) {
      await supabase.from('fornecedores').update(payload).eq('id', editForn.id)
      show('✅ Salvo!')
    } else {
      await supabase.from('fornecedores').insert([payload])
      show('✅ Fornecedor cadastrado!')
      closeForm()
    }
    setSaving(false)
    fetchFornecedores()
  }

  async function deletar(forn) {
    const { count } = await supabase.from('item_fornecedores').select('*', { count: 'exact', head: true }).eq('fornecedor_id', forn.id)
    if (count > 0) {
      show(`"${forn.nome}" tem ${count} item(ns) vinculado(s) — remova os vínculos primeiro`)
      setConfirm(null)
      return
    }
    await supabase.from('fornecedores').delete().eq('id', forn.id)
    show('Fornecedor removido')
    setConfirm(null)
    closeForm()
    fetchFornecedores()
  }

  async function salvarCusto(row) {
    const novo = parseFloat(custoInput)
    if (isNaN(novo) || novo < 0) { show('Custo inválido'); return }
    await supabase.from('item_fornecedores').update({ custo: novo }).eq('id', row.id)
    setFornItens(prev => prev.map(r => r.id === row.id ? { ...r, custo: novo } : r))
    setEditingCusto(null)
    show('✅ Custo atualizado')
  }

  async function removerVinculo(row) {
    await supabase.from('item_fornecedores').delete().eq('id', row.id)
    setFornItens(prev => prev.filter(r => r.id !== row.id))
    show('Vínculo removido')
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h1 className="page-title" style={{ margin: 0 }}>Fornecedores</h1>
        <button className="btn btn-primary btn-sm" onClick={() => openForm()}>+ Novo</button>
      </div>

      {loading && <div className="spinner">Carregando...</div>}

      {!loading && fornecedores.length === 0 && (
        <div className="empty">
          <div className="empty-icon">🏪</div>
          <p>Nenhum fornecedor ainda.</p>
          <br />
          <button className="btn btn-ghost btn-sm" onClick={() => openForm()}>Cadastrar primeiro</button>
        </div>
      )}

      {!loading && fornecedores.length > 0 && (
        <div className="card" style={{ padding: '4px 16px' }}>
          {fornecedores.map(forn => (
            <div key={forn.id} className="list-item">
              <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => openForm(forn)}>
                <div className="item-name">{forn.nome}</div>
                {forn.telefone && <div className="item-sub">{forn.telefone}</div>}
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                {forn.telefone && (
                  <a href={whatsappUrl(forn.telefone)} target="_blank" rel="noopener noreferrer"
                    style={{ fontSize: 20, textDecoration: 'none', lineHeight: 1 }}>💬</a>
                )}
                <span style={{ fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }} onClick={() => openForm(forn)}>editar ›</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeForm()}>
          <div className="modal" style={{ maxHeight: '88vh', overflowY: 'auto' }}>
            <div className="modal-handle" />
            <div className="modal-title">{editForn ? 'Editar fornecedor' : 'Novo fornecedor'}</div>

            <div className="field">
              <label>Nome</label>
              <input
                placeholder="Ex: Mercado das Flores, João..."
                value={form.nome}
                onChange={e => setForm({ ...form, nome: e.target.value })}
                autoFocus
              />
            </div>
            <div className="field">
              <label>Telefone</label>
              <input
                placeholder="(00) 00000-0000"
                value={form.telefone}
                onChange={e => setForm({ ...form, telefone: e.target.value })}
                type="tel"
              />
            </div>
            <button className="btn btn-primary" onClick={salvar} disabled={saving}>
              {saving ? 'Salvando...' : editForn ? 'Salvar dados' : 'Cadastrar'}
            </button>
            {editForn?.telefone && (
              <a href={whatsappUrl(editForn.telefone)} target="_blank" rel="noopener noreferrer"
                style={{ display: 'block', textAlign: 'center', marginTop: 10, fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}>
                💬 Conversar no WhatsApp
              </a>
            )}

            {editForn && (
              <>
                <div style={{
                  margin: '20px 0 10px',
                  fontWeight: 600, fontSize: 13,
                  color: 'var(--text-secondary)',
                  borderTop: '1px solid var(--border)',
                  paddingTop: 16,
                }}>
                  Itens deste fornecedor
                </div>

                {loadingItens && <div className="spinner">Carregando...</div>}

                {!loadingItens && fornItens.length === 0 && (
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: 12 }}>
                    Nenhum item vinculado ainda.
                  </div>
                )}

                {!loadingItens && fornItens.map(row => (
                  <div key={row.id} style={{
                    background: 'var(--bg)', borderRadius: 8, padding: '10px 12px',
                    marginBottom: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.itens?.nome}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                        {row.itens?.categoria} · por {row.itens?.unidade}
                      </div>
                      <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                        {row.link_compra && (
                          <a href={row.link_compra} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: 18, textDecoration: 'none', lineHeight: 1 }}>🔗</a>
                        )}
                        {editForn?.telefone && (
                          <a href={whatsappUrl(editForn.telefone)} target="_blank" rel="noopener noreferrer"
                            style={{ fontSize: 18, textDecoration: 'none', lineHeight: 1 }}>💬</a>
                        )}
                      </div>
                    </div>

                    {editingCusto === row.id ? (
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
                        <input
                          type="number" min="0" step="0.01"
                          value={custoInput}
                          onChange={e => setCustoInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') salvarCusto(row); if (e.key === 'Escape') setEditingCusto(null) }}
                          style={{ width: 80 }}
                          autoFocus
                        />
                        <button className="btn btn-primary btn-sm" onClick={() => salvarCusto(row)}>ok</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setEditingCusto(null)}>×</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                        <span
                          style={{ fontWeight: 700, fontSize: 15, color: 'var(--primary)', cursor: 'pointer' }}
                          onClick={() => { setEditingCusto(row.id); setCustoInput(row.custo) }}
                          title="Toque para editar"
                        >
                          R${parseFloat(row.custo).toFixed(2)}
                        </span>
                        <button className="btn btn-danger btn-sm" onClick={() => removerVinculo(row)}>×</button>
                      </div>
                    )}
                  </div>
                ))}

                <button
                  className="btn btn-danger"
                  style={{ marginTop: 16 }}
                  onClick={() => setConfirm({ msg: `Remover "${editForn.nome}"?`, fn: () => deletar(editForn) })}
                >
                  Remover fornecedor
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {confirm && (
        <div className="modal-overlay" style={{ zIndex: 100 }} onClick={() => setConfirm(null)}>
          <div className="modal" style={{ paddingBottom: 20 }} onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <p style={{ fontSize: 15, marginBottom: 20, textAlign: 'center' }}>{confirm.msg}</p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setConfirm(null)}>Cancelar</button>
              <button className="btn btn-danger" style={{ flex: 1 }} onClick={confirm.fn}>Remover</button>
            </div>
          </div>
        </div>
      )}

      <div className={`toast ${visible ? 'show' : ''}`}>{msg}</div>
    </div>
  )
}
